import styled from "styled-components";

const CONTACT_EMAIL = "support@biterecipes.co";

function Privacy() {
  return (
    <Container>
      <Section>
        <PageTitle>Privacy Policy for Bite</PageTitle>
        <Body>
          <div>
            <strong>Effective Date:</strong> October 18, 2026
          </div>
          <div>
            At Bite, your privacy is important to us. This Privacy Policy
            explains what information we collect when you use the Bite app, the
            website at biterecipes.co, the Bite connector for AI assistants such
            as Claude and ChatGPT, and related services (together, “Bite”), how
            we use and share it, and the choices you have. It should be
            read together with our <a href="/terms">Terms of Service</a>.
          </div>
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>Information We Collect</Title>
        <Body>
          <ul>
            <li>
              <strong>Account information:</strong> When you sign up or sign
              in, we collect your email address, your name if you provide it,
              and any profile information you choose to add, such as a username
              or profile photo.
            </li>
            <li>
              <strong>Content you create:</strong> Recipes, photos,
              collections, notes, meal plans, shopping lists, reviews, replies
              and other content you add to Bite.
            </li>
            <li>
              <strong>Photos:</strong> If you allow access to your photo
              library, Bite uses only the photos you choose to add, such as
              recipe or profile photos.
            </li>
            <li>
              <strong>Content you import:</strong> When you save a recipe from
              Instagram, TikTok, a website, a photo or another source, we
              receive the link, text or image you share with Bite so we can
              turn it into a recipe.
            </li>
            <li>
              <strong>Questions you ask:</strong> If you use features that let
              you ask questions about a recipe, we receive your questions and
              the recipe they relate to so we can answer them.
            </li>
            <li>
              <strong>Connected AI assistants:</strong> If you connect an AI
              assistant to Bite, we keep a record of the connection (which
              assistant, when you connected and when it was last used) and the
              security tokens that let it reach your account. When your
              assistant uses Bite, we receive the requests it sends, such as a
              search, a recipe or link to save, or meal plan changes, so we can
              carry them out. We keep a log of which Bite actions were used,
              by which assistant, and whether they worked, and we note which
              assistant saved each recipe, but not the recipes, notes or other
              content involved. We do not receive your conversations with the
              assistant, only the requests it sends to Bite. If you ask your
              assistant to report a problem to us, we receive the report it
              writes, which may describe the recipe, meal plan or shopping list
              involved. We use it to fix the problem and delete it if you delete
              your account.
            </li>
            <li>
              <strong>Community activity:</strong> Who you follow and who
              follows you, and the recipes and collections you save, cook,
              share or interact with.
            </li>
            <li>
              <strong>Purchases:</strong> If you buy a subscription or other
              purchase, Apple or Google processes the payment. We receive
              information about the purchase and subscription status, but not
              your payment card details.
            </li>
            <li>
              <strong>Notifications:</strong> If you allow push notifications,
              we receive a device token so we can send them.
            </li>
            <li>
              <strong>Automatically collected information:</strong> Usage data,
              such as how you interact with the app, device information, such
              as your operating system and app version, and crash and
              performance logs. When you visit our website, our hosting
              provider may log your IP address, browser type and the pages you
              request.
            </li>
            <li>
              <strong>App events, usage analytics and campaign
              information:</strong> We record how you use the Bite app, such as
              the screens you open, recipes you view, save, cook or add to your
              meal plan, imports, searches and subscription checkouts, so we can
              understand which features are used, improve the app and measure
              how well our marketing works. These events are linked to your
              Bite account ID and include your username, device type,
              operating system, app version, subscription status and whether
              you arrived through one of our marketing links. We do not include
              the text of your recipes, notes, reviews or Ask Bite messages in
              these events. For searches, we record only the number of results,
              plus the words of a recipe search that found nothing, with
              anything that looks like an email address, phone number or link
              left out.
            </li>
            <li>
              <strong>Advertising information:</strong> If ads are shown in
              your version of Bite, our advertising partners may collect or
              receive information such as your IP address, device information,
              app activity, ad views, ad clicks and general location inferred
              from your IP address to show, measure and improve ads.
            </li>
          </ul>
          <div>
            We do not collect your precise location or your contacts.
          </div>
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>How We Use Your Information</Title>
        <Body>
          We use the information we collect to:
          <ul>
            <li>Create and manage your account and sign you in.</li>
            <li>
              Save, import, organize and display your recipes, and turn
              imported content into recipes.
            </li>
            <li>
              Provide community features, such as public profiles, following,
              sharing and discovery.
            </li>
            <li>Answer questions you ask about recipes.</li>
            <li>
              Let AI assistants you connect search your recipes and public
              Bite recipes, and update your library, collections, meal plan
              and shopping list when you ask them to.
            </li>
            <li>Generate images for recipes that don’t have one.</li>
            <li>Provide paid features and manage subscriptions.</li>
            <li>
              Keep Bite safe, including automatically screening uploaded photos
              and usernames, reviewing reports, preventing abuse and enforcing
              our Terms of Service.
            </li>
            <li>
              Understand how Bite is used, fix crashes and improve the app.
            </li>
            <li>
              Send notifications and communicate with you about updates,
              support or feedback.
            </li>
            <li>
              Display and measure ads and sponsored content, and measure our
              marketing campaigns.
            </li>
            <li>Comply with legal obligations.</li>
          </ul>
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>What Other People Can See</Title>
        <Body>
          Your profile name, username and photo, the recipes and collections
          you make public, and community activity such as follows may be
          visible to other Bite users. Public content can also be opened by
          anyone who has a share link to it. Content you keep private is not
          shown to other users.
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>Connected AI Assistants</Title>
        <Body>
          <div>
            You can choose to connect Bite to an AI assistant, such as Claude or
            ChatGPT. You approve each connection yourself after signing in to
            Bite. The connector is free to use. Meal planning and shopping lists
            through it need an active Bite Pro subscription, as they do in the
            app. Our <a href="/connect">connector page</a> explains how it
            works.
          </div>
          <div>
            While connected, your assistant can access:
            <ul>
              <li>
                recipes you have created, saved, favorited or marked as cooked
              </li>
              <li>
                collections you own or that others have shared with you
              </li>
              <li>your meal plan and shopping list</li>
              <li>your private recipe notes</li>
              <li>public recipes on Bite</li>
            </ul>
            It cannot access your email address, password or payment details,
            or other users’ private recipes.
          </div>
          <div>
            When you ask, your assistant can search public Bite recipes, save
            and favorite recipes, create collections and add recipes to them,
            create, import or edit your own recipes, plan meals, update your
            shopping list, add private notes and mark recipes as cooked.
            Recipes saved through an assistant are private. If a recipe
            doesn’t have an approved photo, it is saved as a draft until you
            add one in Bite. Your assistant cannot delete anything, edit other
            people’s recipes, change a recipe’s privacy, post reviews or follow
            people. If it edits one of your recipes that is already public,
            the change is visible to others, just as if you made it in the
            app. Marking a recipe as cooked notifies the recipe’s creator, just
            as it does in the app.
          </div>
          <div>
            You can disconnect an assistant at any time from{" "}
            <a href="/app/settings/connected-apps">
              Settings → Connected apps
            </a>{" "}
            on biterecipes.co. Access ends immediately. Connections may also
            expire and ask you to sign in again. Deleting your Bite account
            removes all of your connections. Information your assistant already
            received stays with the assistant’s provider under its own policies.
          </div>
          <div>
            We keep a log of which Bite actions your assistant used, without
            recipe or other content, for 90 days to keep the connector secure
            and working, and we keep usage counts, including which assistant
            was used, to understand how the connector is used, including
            through PostHog.
          </div>
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>How We Share Your Information</Title>
        <Body>
          We do not sell your personal information for money. We share
          information only in the following situations:
          <ul>
            <li>
              <strong>With other users:</strong> As described in “What Other
              People Can See” above.
            </li>
            <li>
              <strong>With AI assistants you connect:</strong> If you connect
              an AI assistant, such as Anthropic’s Claude or OpenAI’s ChatGPT,
              we share the Bite information it asks for on your behalf. See
              “Connected AI Assistants” below for exactly what that includes.
              The company that provides the assistant handles that information
              under its own terms and privacy policy, not ours, and may keep it
              as part of your conversation history.
            </li>
            <li>
              <strong>With service providers:</strong> These providers process
              information on our behalf and only as needed to provide their
              services to us:
              <ul>
                <li>
                  Google Firebase and Google Cloud, for sign-in, databases,
                  file storage, hosting, notifications and app infrastructure.
                </li>
                <li>
                  Google Gemini, an artificial intelligence service, to import
                  and format recipes from the links and content you share, to
                  answer questions you ask about recipes, to screen the recipe
                  and profile photos you upload or that come from links you
                  import (including through a connected AI assistant), and to
                  generate recipe images.
                </li>
                <li>
                  Firebase Crashlytics and Sentry, to report crashes and errors
                  so we can fix them.
                </li>
                <li>
                  PostHog, Inc. (United States), for product analytics. PostHog
                  processes usage events on our behalf and does not use them
                  for its own purposes or for advertising.
                </li>
              </ul>
            </li>
            <li>
              <strong>With Meta:</strong> We use the Meta (Facebook) SDK to
              send Meta the app events described above, such as sign-ups,
              recipe views, searches, saves and subscription checkouts, and we
              use Meta App Links to tell which of our campaigns brought you to
              Bite. This helps us measure and attribute our advertising. We
              have turned off the SDK’s advertiser ID collection and
              advertiser tracking, so Meta does not receive your device’s
              advertising identifier from Bite.
            </li>
            <li>
              <strong>With advertising partners:</strong> We use Google AdMob
              to display ads in Bite. Bite currently shows only
              non-personalized ads, and asks for consent where required. Google
              may still collect or use information such as IP address, device
              information and ad interactions to serve and measure ads and to
              prevent fraud. Under some U.S. state laws, sharing app activity
              with advertising and measurement partners may be considered
              “sharing” or “targeted advertising.” You can learn more about how
              Google uses data from apps that use its services at{" "}
              <a
                href="https://policies.google.com/technologies/partner-sites"
                target="_blank"
                rel="noreferrer"
              >
                policies.google.com/technologies/partner-sites
              </a>
              .
            </li>
            <li>
              <strong>With app stores:</strong> Apple and Google process
              purchases and subscriptions under their own privacy policies.
            </li>
            <li>
              <strong>For legal reasons:</strong> If required by law, to
              respond to valid requests from public authorities, or to protect
              the rights, safety and security of Bite, our users or others.
            </li>
            <li>
              <strong>Business transfers:</strong> If Bite is involved in a
              merger, acquisition or sale of assets, your information may be
              transferred as part of that transaction, subject to this Privacy
              Policy.
            </li>
          </ul>
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>Ads, Sponsored Content, and Privacy Choices</Title>
        <Body>
          <div>
            Bite may show ads or sponsored content in feeds, discovery surfaces
            or other parts of the app. Sponsored items may link to third-party
            websites, apps, products or services. Those third parties are
            responsible for their own privacy practices.
          </div>
          <div>
            Where required, Bite or our advertising partners ask for your
            consent before using information for certain advertising-related
            purposes. You may also be able to manage ad, tracking or privacy
            choices through in-app prompts, your device settings, Google
            settings or other controls made available by our partners.
          </div>
          <div>
            On iOS, you may be asked whether to allow tracking under Apple’s App
            Tracking Transparency framework. On Android and iOS, your app store
            privacy disclosures may also describe data used for advertising,
            analytics and app functionality.
          </div>
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>How Long We Keep Information</Title>
        <Body>
          We keep your information for as long as your account is active or as
          needed to provide Bite. When you delete your account, we delete or
          anonymize your personal information and content within 30 days,
          except for information we must keep to comply with legal
          obligations, resolve disputes, prevent abuse or enforce our
          agreements, and copies that remain in backups for a limited time.
          If you connect an AI assistant, we keep the connection until you
          disconnect it, it expires or you delete your account, and we keep the
          log of which connector actions were used for 90 days.
          Usage analytics events are kept for up to 12 months. When you delete
          your account, we also delete your analytics history from PostHog,
          keeping only one anonymous record that an account was deleted, which
          is not linked to you.
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>Data Security</Title>
        <Body>
          We take data security seriously and use measures designed to protect
          your personal information, including:
          <ul>
            <li>
              Storing your data with Firebase, which uses industry-standard
              encryption and safeguards.
            </li>
            <li>
              Limiting access to your personal information to authorized
              people who need it.
            </li>
            <li>
              Letting connected AI assistants reach your account only through
              secure, revocable sign-in tokens. We never give an assistant your
              password.
            </li>
          </ul>
          However, no method of transmission over the internet or electronic
          storage is completely secure, and we cannot guarantee absolute
          security.
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>Your Choices and Rights</Title>
        <Body>
          <ul>
            <li>
              <strong>Account information:</strong> You can update your email
              or profile information within the app.
            </li>
            <li>
              <strong>Content:</strong> You can edit or delete recipes and
              other content you’ve added at any time.
            </li>
            <li>
              <strong>Connected AI assistants:</strong> You can see and
              disconnect the assistants connected to your account at any time
              from{" "}
              <a href="/app/settings/connected-apps">
                Settings → Connected apps
              </a>{" "}
              on biterecipes.co. You can also remove Bite from the assistant’s
              own settings.
            </li>
            <li>
              <strong>Account deletion:</strong> You can ask us to delete your
              account and the personal information and content associated with
              it. See <a href="/delete-account">Delete Your Account</a> for how.
            </li>
            <li>
              <strong>Device permissions:</strong> You can turn off photo
              library access and push notifications at any time in your
              device settings.
            </li>
            <li>
              <strong>Advertising choices:</strong> You can use in-app privacy
              prompts, device settings and advertising partner controls to
              manage certain advertising preferences where those choices are
              available.
            </li>
            <li>
              <strong>Privacy rights:</strong> Depending on where you live,
              including in the European Economic Area, the United Kingdom,
              California and other U.S. states, you may have the right to
              access, correct, delete or receive a copy of your personal
              information, to object to or restrict certain processing, to opt
              out of targeted advertising, and to withdraw consent. To make a
              request, email us at {CONTACT_EMAIL}. We may need to verify your
              identity before responding, and we will not discriminate against
              you for exercising these rights. You may also have the right to
              complain to your local data protection authority.
            </li>
          </ul>
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>International Users</Title>
        <Body>
          Bite is operated from the United States, and our service providers
          may process information in the United States and other countries.
          Those countries may have data protection laws that differ from the
          laws where you live. Where required, we rely on appropriate
          safeguards for these transfers.
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>Children’s Privacy</Title>
        <Body>
          Bite is not intended for children under the age of 13. We do not
          knowingly collect personal information from children under 13. If we
          learn that a child under 13 has provided us with personal
          information, we will delete it promptly.
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>Changes to This Privacy Policy</Title>
        <Body>
          We may update this Privacy Policy from time to time. We will post the
          updated policy on this page and update the effective date, and we
          will notify you in the app or by email before material changes take
          effect.
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>Contact Us</Title>
        <Body>
          <div>
            If you have any questions about this Privacy Policy or how we
            handle your information, please contact us at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </div>
        </Body>
      </Section>
    </Container>
  );
}

export default Privacy;

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 60px;
  padding-top: 0px;
  @media (max-width: 590px) {
    padding: 30px;
    padding-top: 0px;
    padding-bottom: 60px;
  }
`;

const Section = styled.section`
  padding-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Divider = styled.div`
  width: 100%;
  height: 0px;
  border-top: 0.5px solid black;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 35px;
  font-weight: 800;
  font-family: sans-serif;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 25px;
  font-weight: 800;
  font-family: sans-serif;
`;

const Body = styled.div`
  font-size: 18px;
  font-weight: 500;
  font-family: sans-serif;
  line-height: 1.6;
  display: flex;
  flex-direction: column;
  gap: 10px;

  strong {
    font-weight: 600;
  }

  a {
    color: inherit;
  }

  ul {
    margin-top: 10px;
    padding-left: 20px;

    li {
      margin-bottom: 8px;
    }
  }
`;
